import React, { useState, useEffect, useContext } from "react";
import Select from "react-select";
import { AuthContext } from "../../../context/authcontext";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import {
  SalesActivityService,
  SalesActivityType,
} from "./salesActivity.service";
import { resolveActivityIcon } from "../../../components/IconPicker/activityIcons";

interface SalesActivityModalProps {
  isVisible: boolean;
  onClose: () => void;
  crmEntityId?: number | null;
  recordId?: number | null;
  onSuccess: () => void;
}

const SalesActivityModal: React.FC<SalesActivityModalProps> = ({
  isVisible,
  onClose,
  crmEntityId,
  recordId,
  onSuccess,
}) => {
  const { selectedTenantId, auth } = useContext(AuthContext);

  const [activityTypes, setActivityTypes] = useState<SalesActivityType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(
    null
  );
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setSelectedTypeId(null);
    setSelectedStatusId(null);
    setNotes("");
  };

  useEffect(() => {
    if (!isVisible) {
      resetForm();
    }
  }, [isVisible]);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!isVisible || !selectedTenantId) return;
      setLoadingTypes(true);
      try {
        const types = await SalesActivityService.getActivityTypes(
          Number(selectedTenantId)
        );
        setActivityTypes(types);
      } catch (error) {
        console.error("Error fetching sales activity types", error);
      } finally {
        setLoadingTypes(false);
      }
    };
    fetchTypes();
  }, [isVisible, selectedTenantId]);

  if (!isVisible) return null;

  const selectedType = activityTypes.find(
    (t) => t.crm_sales_activity_type_id === selectedTypeId
  );

  const typeOptions = activityTypes.map((t) => ({
    value: t.crm_sales_activity_type_id,
    label: t.crm_sales_activity_type_display_name,
    icon: resolveActivityIcon(t.icon, t.crm_sales_activity_type_display_name),
  }));

  const statusOptions = (selectedType?.statuses || []).map((s) => ({
    value: s.crm_sales_activity_type_status_id,
    label: s.crm_sales_activity_type_status_display_name,
  }));

  const handleTypeChange = (option: any) => {
    setSelectedTypeId(option?.value ?? null);
    setSelectedStatusId(null);
  };

  const handleSubmit = async () => {
    if (
      !selectedTenantId ||
      !selectedTypeId ||
      !selectedStatusId
    )
      return;

    if (!crmEntityId || !recordId) {
      console.error(
        "SalesActivityModal: missing crmEntityId/recordId, cannot submit",
        { crmEntityId, recordId }
      );
      showErrorAlert(
        "Unable to log this activity: could not determine the record to attach it to."
      );
      return;
    }

    setSubmitting(true);
    try {
      await SalesActivityService.createActivityLog({
        tenant_id: Number(selectedTenantId),
        crm_entity_id: crmEntityId,
        record_id: recordId,
        crm_sales_activity_type_id: selectedTypeId,
        crm_sales_activity_type_status_id: selectedStatusId,
        notes: notes.trim() || undefined,
        is_active: true,
        created_by: auth?.user_id,
        updated_by: auth?.user_id,
      });
      showSuccessAlert("Sales activity logged successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error logging sales activity", error);
      showErrorAlert("Failed to log sales activity");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", backdropFilter: "blur(2px)" }}
        role="dialog"
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "700px" }}
        >
          <div
            className="modal-content shadow-lg border-0 rounded-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header border-0 pb-0 pt-4 px-4">
              <h4 className="modal-title d-flex align-items-center fw-bold">
                <div className="avatar avatar-sm bg-light-info text-info rounded-circle me-3 d-flex align-items-center justify-content-center">
                  <i className="ti ti-clipboard-list fs-20" />
                </div>
                Log Activity
              </h4>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            <div className="modal-body px-4 pt-4 pb-2">
              <div className="mb-3">
                <label className="form-label fw-semibold text-muted mb-2">
                  Activity Type <span className="text-danger">*</span>
                </label>
                <Select
                  isLoading={loadingTypes}
                  options={typeOptions}
                  value={
                    typeOptions.find((o) => o.value === selectedTypeId) ||
                    null
                  }
                  onChange={handleTypeChange}
                  placeholder="Select activity type"
                  classNamePrefix="react-select"
                  isClearable
                  formatOptionLabel={(option: any) => (
                    <span className="d-flex align-items-center">
                      <i className={`${option.icon} me-2`} />
                      {option.label}
                    </span>
                  )}
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold text-muted mb-2">
                  Status <span className="text-danger">*</span>
                </label>
                <Select
                  options={statusOptions}
                  value={
                    statusOptions.find((o) => o.value === selectedStatusId) ||
                    null
                  }
                  onChange={(option: any) =>
                    setSelectedStatusId(option?.value ?? null)
                  }
                  placeholder={
                    selectedTypeId
                      ? "Select status"
                      : "Select an activity type first"
                  }
                  classNamePrefix="react-select"
                  isClearable
                  isDisabled={!selectedTypeId}
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold text-muted mb-2">
                  Notes
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Add notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 px-4 pb-4">
              <button
                type="button"
                className="btn btn-light rounded-pill px-4"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary rounded-pill px-4"
                onClick={handleSubmit}
                disabled={submitting || !selectedTypeId || !selectedStatusId}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ti ti-device-floppy me-2" /> Save Activity
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ opacity: 0.5 }}
      />
    </>
  );
};

export default SalesActivityModal;
